/**
 * The trigger of a list's filters (foundations §7.14, hybrid-3c §7): an
 * outlined button at rest, contained while the filters are shown, with the
 * number of active filters as a badge. Below `sm` it is an icon button.
 */

import React from 'react';
import { useTranslate } from 'react-admin';
import { Badge, Button, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { FilterListRounded as FilterIcon } from '@mui/icons-material';

export interface FilterToggleProps {
  open: boolean;
  onToggle: () => void;
  /** Filters currently narrowing the list. */
  activeCount?: number;
}

export const FilterToggle: React.FC<FilterToggleProps> = ({ open, onToggle, activeCount = 0 }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const label = translate('common.filters.label');

  if (isPhone) {
    return (
      <Tooltip title={label}>
        <IconButton onClick={onToggle} aria-label={label} aria-pressed={open}>
          <Badge badgeContent={activeCount} invisible={activeCount === 0}>
            <FilterIcon />
          </Badge>
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Badge badgeContent={activeCount} invisible={activeCount === 0} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <Button variant={open ? 'contained' : 'outlined'} startIcon={<FilterIcon />} onClick={onToggle} aria-pressed={open}>
        {label}
      </Button>
    </Badge>
  );
};

export default FilterToggle;
